import type { QueryClient } from "@tanstack/react-query"
import {
  loadDeclarationScope,
  loadFrameworkSlice,
} from "../../../../../api/declarationsApi"
import { frameworkStateFromSlice } from "../loadFrameworkState"
import type { StatementCard } from "../useStudioV2State"
import {
  buildApprovedSnapshot,
  type ApprovedSnapshot,
} from "./approvedSnapshot"

const SNAPSHOT_CONCURRENCY = 2

/** `fn` over `items`, at most `limit` running at once; results in `items`' order. */
export async function mapLimited<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  const worker = async () => {
    while (next < items.length) {
      const index = next++
      results[index] = await fn(items[index])
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  )
  return results
}

/** `mapLimited` that never gives up the loads that worked: every item settles, in
 *  `items`' order, so a caller keeps what loaded and can name what did not. */
export function mapLimitedSettled<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  return mapLimited(items, limit, (item) =>
    fn(item).then(
      (value): PromiseSettledResult<R> => ({ status: "fulfilled", value }),
      (reason: unknown): PromiseSettledResult<R> => ({
        status: "rejected",
        reason,
      }),
    ),
  )
}

/**
 * STEP C3-b2: THE APPROVED SNAPSHOT IN THE PAGED REVIEW.
 *
 * The approval dialog's matrix shows every regulation's verdict per article. The paged
 * review holds the open regulation only, so at approval it loads every regulation's
 * slice and the supplier pool (`fetchQuery`: an invalidated slice refetches, never a
 * stale cached one), builds each regulation's cards exactly as the open one is built
 * (`frameworkStateFromSlice`, the C3-b3 gestures' own helper), and hands them
 * to the same `buildApprovedSnapshot` flag off uses.
 */
export async function loadPagedApprovedSnapshot(input: {
  queryClient: QueryClient
  declId: string
  frameworks: string[]
  nameOf: (framework: string) => string
}): Promise<ApprovedSnapshot> {
  const { queryClient, declId, frameworks, nameOf } = input
  // At most two downloads at a time: the slices of a big declaration are megabytes
  // each, and the approval moment should not hit the server with all of them at once.
  // Awaited together: whichever read fails first rejects the snapshot at once, and the
  // other one is still handled (never an unhandled rejection, never left un-awaited).
  const [scope, slices] = await Promise.all([
    loadDeclarationScope(queryClient, declId),
    mapLimited(frameworks, SNAPSHOT_CONCURRENCY, (fw) =>
      loadFrameworkSlice(queryClient, declId, fw),
    ),
  ])
  const cardsByFramework = new Map<string, StatementCard[]>()
  for (const slice of slices) {
    const { framework, cards } = frameworkStateFromSlice(slice)
    cardsByFramework.set(framework, cards)
  }
  const pool = scope?.articlePool?.length
    ? scope.articlePool
    : (scope?.candidateArticles ?? [])
  return buildApprovedSnapshot({ frameworks, nameOf, cardsByFramework, pool })
}
